import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { authenticatedApi } from "@/lib/api";
import { Bell } from "@phosphor-icons/react";

export default function NotificationsBell({ user }) {
    const [unread, setUnread] = useState(0);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await authenticatedApi.get("/notifications", { params: { limit: 10 } });
            setUnread(data.unread);
            setItems(data.items);
        } catch {}
    }, [user]);

    useEffect(() => {
        if (!user) { setUnread(0); setItems([]); return; }
        load();
        const t = setInterval(load, 45000);
        const refresh = () => load();
        window.addEventListener("campodirecto:messages-updated", refresh);
        window.addEventListener("storage", refresh);
        return () => {
            clearInterval(t);
            window.removeEventListener("campodirecto:messages-updated", refresh);
            window.removeEventListener("storage", refresh);
        };
    }, [user, load]);

    const markAll = async () => {
        try { await authenticatedApi.post("/notifications/read-all"); load(); } catch {}
    };

    const openNotification = async (notification) => {
        setOpen(false);
        if (!notification.read) {
            try { await authenticatedApi.post(`/notifications/${notification.id}/read`); } catch {}
        }
    };

    if (!user) return null;

    return (
        <div className="relative">
            <button onClick={() => { setOpen(!open); if (!open) load(); }}
                className="relative p-2 rounded-full hover:bg-secondary" data-testid="notifications-bell" aria-label="Notificaciones">
                <Bell size={20} weight={unread > 0 ? "fill" : "regular"} className={unread > 0 ? "text-[#2E7D32]" : ""} />
                {unread > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 rounded-full bg-[#E8A33A] text-white text-[10px] font-bold px-1 grid place-items-center" data-testid="notifications-count">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-border rounded-2xl shadow-xl overflow-hidden z-50" data-testid="notifications-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <div className="font-display font-bold">Notificaciones</div>
                        {unread > 0 && (
                            <button onClick={markAll} className="text-xs text-[#2E7D32] font-semibold hover:underline">Marcar todo leído</button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="p-6 text-center text-sm text-muted-foreground">Sin notificaciones aún.</div>
                        ) : items.map((n) => (
                            <Link key={n.id} to={n.url || "/dashboard"} onClick={() => openNotification(n)}
                                className={`block px-4 py-3 border-b border-border hover:bg-secondary/50 ${!n.read ? "bg-[#2E7D32]/5" : ""}`}
                                data-testid={`notif-${n.id}`}>
                                <div className="text-sm font-semibold">{n.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>
                                <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("es-MX")}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
